// src/store/store.js
import { createStore } from 'vuex'; // For Vue 3, use 'import Vue from "vue"; import Vuex from "vuex"' for Vue 2
import { jwtDecode } from 'jwt-decode';
import { cartApi, wishlistApi, productApi } from '@/services/cartWishlistApi';
import { toastService } from '@/services/toast';
import { analytics } from '@/services/analytics';

// ---- helpers ---------------------------------------------------------------

// The cart/wishlist APIs require an explicit userId. It isn't in the login
// response, but it is encoded in the JWT payload as `id`.
function decodeUserId(token) {
    try {
        return token ? (jwtDecode(token).id ?? null) : null;
    } catch (e) {
        return null;
    }
}

// A line's identity is book + variant meta (coverType + printType).
function sameVariant(a, b) {
    return (a?.meta?.coverType ?? null) === (b?.meta?.coverType ?? null)
        && (a?.meta?.printType ?? null) === (b?.meta?.printType ?? null);
}

function variantMeta(variant) {
    return {
        coverType: variant?.meta?.coverType ?? null,
        printType: variant?.meta?.printType ?? null,
    };
}

// Guests persist to localStorage; once logged in the server is the source of
// truth, so we stop mirroring to localStorage to avoid double-counting.
function persistCart(state) {
    if (state.userId) return;
    localStorage.setItem('bk_cart', JSON.stringify(state.cart));
}
function persistWishlist(state) {
    if (state.userId) return;
    localStorage.setItem('bk_wishlist', JSON.stringify(state.wishlist));
}

function clone(value) {
    return JSON.parse(JSON.stringify(value));
}

// On error, log the user out for an expired token, otherwise surface a toast.
function handleApiError(commit, error, message) {
    const reason = error?.response?.data?.reason;
    const status = error?.response?.status;
    if (reason === 'INVALID_TOKEN' || status === 403) {
        commit('logout');
        localStorage.removeItem('bk_token');
        localStorage.removeItem('bk_user');
        localStorage.removeItem('bk_passwordResetNeeded');
        toastService.show({
            message: 'Your session expired. Please log in again.',
            status: 'error',
            duration: 3000,
        });
        return;
    }
    if (message) {
        toastService.show({ message, status: 'error', duration: 3000 });
    }
}

// Match a server line's variant meta against the product's live variants[] to
// recover price/salePrice/stock; fall back to the first variant.
function pickVariant(product, lineVariant) {
    const variants = product?.variants ?? [];
    return variants.find(v => sameVariant(v, lineVariant)) || variants[0] || null;
}

function serverBookId(line) {
    return line?.book?._id ?? line?.book ?? null;
}

// Build a store cart item (matches the shape produced by the addToCart mutation).
function buildCartItem(line, products) {
    const bookId = serverBookId(line);
    const product = products[bookId] || null;
    const v = pickVariant(product, line.variant);
    return {
        _id: bookId,
        title: product?.title ?? line.book?.title ?? '',
        author: product?.author ?? '',
        images: product?.images ?? [],
        productCategory: product?.productCategory ?? '',
        quantity: line.quantity ?? 1,
        variant: {
            name: line.variant?.name ?? v?.name ?? '',
            price: v?.price ?? line.book?.price ?? 0,
            salePrice: v?.salePrice ?? null,
            stock: v?.stock ?? 0,
            meta: {
                coverType: line.variant?.meta?.coverType ?? v?.meta?.coverType ?? null,
                printType: line.variant?.meta?.printType ?? v?.meta?.printType ?? null,
            },
        },
    };
}

// Build a store wishlist item (WishList.vue reads images/title/author/price and
// uses minVariant for "add to cart").
function buildWishlistItem(line, products) {
    const bookId = serverBookId(line);
    const product = products[bookId] || null;
    const v = pickVariant(product, line.variant);
    const variant = v
        ? {
            name: line.variant?.name ?? v.name ?? '',
            price: v.price ?? line.book?.price ?? 0,
            salePrice: v.salePrice ?? null,
            stock: v.stock ?? 0,
            meta: variantMeta(line.variant?.meta ? line.variant : v),
        }
        : (line.variant
            ? { name: line.variant.name ?? '', price: line.book?.price ?? 0, salePrice: null, stock: 0, meta: variantMeta(line.variant) }
            : null);
    return {
        _id: bookId,
        title: product?.title ?? line.book?.title ?? '',
        author: product?.author ?? '',
        images: product?.images ?? [],
        productCategory: product?.productCategory ?? '',
        price: variant?.salePrice ?? variant?.price ?? line.book?.price ?? 0,
        variant,
        minVariant: variant,
    };
}

// Fetch every product referenced by the given server lines, once each.
async function fetchProducts(lines) {
    const ids = [...new Set(lines.map(serverBookId).filter(Boolean))];
    const products = {};
    await Promise.all(ids.map(async id => {
        try {
            const res = await productApi.getById(id);
            products[id] = res.data;
        } catch (e) {
            products[id] = null;
        }
    }));
    return products;
}

// ---- store -----------------------------------------------------------------

export const store = createStore({
    state: {
        user: JSON.parse(localStorage.getItem('bk_user')) || null,
        token: localStorage.getItem('bk_token') || null,
        userId: decodeUserId(localStorage.getItem('bk_token')),
        cart: JSON.parse(localStorage.getItem('bk_cart')) || [],
        wishlist: JSON.parse(localStorage.getItem('bk_wishlist')) || [],
        passwordResetNeeded: JSON.parse(localStorage.getItem('bk_passwordResetNeeded')) || false,
        isSyncing: false,
    },
    mutations: {
        setUser(state, user) {
            state.user = user;
        },
        setToken(state, token) {
            state.token = token;
        },
        setUserId(state, userId) {
            state.userId = userId;
        },
        setSyncing(state, value) {
            state.isSyncing = value;
        },
        setPasswordResetNeeded(state, passwordResetNeeded) {
            state.passwordResetNeeded = passwordResetNeeded;
        },
        logout(state) {
            state.user = null;
            state.token = null;
            state.userId = null;
            // Clear cart/wishlist so the next guest on this browser starts fresh.
            // Nothing is lost: a logged-in user's data lives in the DB.
            state.cart = [];
            state.wishlist = [];
            localStorage.removeItem('bk_cart');
            localStorage.removeItem('bk_wishlist');
        },
        // Replace the whole cart/wishlist (used for server hydration & rollback).
        setCart(state, cart) {
            state.cart = cart || [];
            persistCart(state);
        },
        setWishlist(state, wishlist) {
            state.wishlist = wishlist || [];
            persistWishlist(state);
        },
        addToCart(state, { product, variant, quantity = 1 }) {
            const cartItem = {
                _id: product._id,
                title: product.title,
                author: product.author,
                images: product.images,
                productCategory: product.productCategory,
                quantity,

                // store full variant snapshot
                variant: {
                    name: variant.name,
                    price: variant.price,
                    salePrice: variant.salePrice ?? null,
                    stock: variant.stock ?? 0,
                    meta: {
                        coverType: variant.meta?.coverType ?? null,
                        printType: variant.meta?.printType ?? null
                    }
                }
            };

            const existingItem = state.cart.find(item =>
                item._id === cartItem._id &&
                sameVariant(item.variant, cartItem.variant)
            );

            if (existingItem) {
                existingItem.quantity += quantity;
            } else {
                state.cart.push(cartItem);
            }

            persistCart(state);
        },
        // Used when user updates quantity from cart page
        updateCartItemQuantity(state, payload) {
            const bookId = payload.productId ?? payload._id;
            const itemInCart = state.cart.find(item =>
                item._id === bookId &&
                sameVariant(item.variant, payload.variant)
            );

            if (itemInCart) {
                itemInCart.quantity = payload.quantity;
            }

            persistCart(state);
        },
        removeFromCart(state, payload) {
            const bookId = payload.productId ?? payload._id;
            state.cart = state.cart.filter(item =>
                !(item._id === bookId && sameVariant(item.variant, payload.variant))
            );

            persistCart(state);
        },
        clearCart(state) {
            state.cart = [];
            localStorage.removeItem('bk_cart');
        },
        addToWishlist(state, book) {
            const itemInWishlist = state.wishlist.find(item =>
                item._id === book._id &&
                sameVariant(item.variant, book.variant)
            );
            if (!itemInWishlist) {
                state.wishlist.push(book);
                persistWishlist(state);
            }
        },
        removeFromWishlist(state, payload) {
            // Accept a plain bookId (legacy) or { bookId, variant }.
            const bookId = typeof payload === 'string' ? payload : payload.bookId;
            const variant = typeof payload === 'string' ? undefined : payload.variant;
            const hasVariant = !!(variant?.meta?.coverType || variant?.meta?.printType);
            state.wishlist = state.wishlist.filter(item => {
                if (item._id !== bookId) return true;
                return hasVariant ? !sameVariant(item.variant, variant) : false;
            });
            persistWishlist(state);
        },
        clearWishlist(state) {
            state.wishlist = [];
            localStorage.removeItem('bk_wishlist');
        },
        loadWishlistFromStorage(state) {
            const storedWishlist = JSON.parse(
                localStorage.getItem('bk_wishlist')
            );
            if (storedWishlist) {
                state.wishlist = storedWishlist;
            }
        },
    },
    actions: {
        login({ commit, dispatch }, { user, token, passwordResetNeeded = false }) {
            commit('setUser', user);
            commit('setToken', token);
            commit('setUserId', decodeUserId(token));
            commit('setPasswordResetNeeded', passwordResetNeeded);
            localStorage.setItem('bk_token', token);
            localStorage.setItem('bk_user', JSON.stringify(user));
            localStorage.setItem('bk_passwordResetNeeded', passwordResetNeeded);
            // Feed advanced matching data to the Meta Pixel now that we know the user.
            analytics.identify(user);
            // Merge any guest cart/wishlist into the account, then load canonical state.
            dispatch('mergeGuestData');
        },
        logout({ commit }) {
            commit('logout');
            localStorage.removeItem('bk_token');
            localStorage.removeItem('bk_user');
            localStorage.removeItem('bk_passwordResetNeeded');
        },
        checkLogin({ commit, dispatch }) {
            const token = localStorage.getItem('bk_token');
            const user = JSON.parse(localStorage.getItem('bk_user'));
            if (token && user) {
                commit('setUser', user);
                commit('setToken', token);
                commit('setUserId', decodeUserId(token));
                dispatch('hydrateFromServer');
            }
        },
        // Push local guest items into the account (sum cart quantities, union
        // wishlist), clear localStorage, then load canonical server state.
        async mergeGuestData({ state, dispatch }) {
            if (!state.userId) return;
            const localCart = clone(state.cart);
            const localWishlist = clone(state.wishlist);

            let serverCart = [];
            try {
                const res = await cartApi.get(state.userId, state.token);
                serverCart = res.data?.cart ?? [];
            } catch (e) {
                // Treat as empty; merge proceeds with local quantities only.
            }

            for (const item of localCart) {
                const match = serverCart.find(s =>
                    serverBookId(s) === item._id && sameVariant(s.variant, item.variant)
                );
                const newQty = (item.quantity ?? 1) + (match?.quantity ?? 0);
                try {
                    await cartApi.upsert({
                        userId: state.userId,
                        bookId: item._id,
                        quantity: newQty,
                        variant: item.variant,
                    }, state.token);
                } catch (e) {
                    // Skip this item; the rest of the merge still proceeds.
                }
            }

            for (const item of localWishlist) {
                try {
                    await wishlistApi.add({
                        userId: state.userId,
                        bookId: item._id,
                        variant: item.variant,
                    }, state.token);
                } catch (e) {
                    // 400 = already in wishlist; safe to ignore.
                }
            }

            localStorage.removeItem('bk_cart');
            localStorage.removeItem('bk_wishlist');
            await dispatch('hydrateFromServer');
        },
        // Load cart + wishlist from the server and enrich for display.
        async hydrateFromServer({ state, commit }) {
            if (!state.userId) return;
            commit('setSyncing', true);
            try {
                const [cartRes, wishRes] = await Promise.allSettled([
                    cartApi.get(state.userId, state.token),
                    wishlistApi.get(state.userId, state.token),
                ]);

                const serverCart = cartRes.status === 'fulfilled'
                    ? (cartRes.value.data?.cart ?? []) : [];
                const serverWishlist = wishRes.status === 'fulfilled'
                    ? (wishRes.value.data?.wishlist ?? []) : [];

                const products = await fetchProducts([...serverCart, ...serverWishlist]);

                commit('setCart', serverCart.map(l => buildCartItem(l, products)).filter(i => i._id));
                commit('setWishlist', serverWishlist.map(l => buildWishlistItem(l, products)).filter(i => i._id));

                // Surface an expired token if either call failed with 403.
                const failed = [cartRes, wishRes].find(r => r.status === 'rejected');
                if (failed) handleApiError(commit, failed.reason, null);
            } finally {
                commit('setSyncing', false);
            }
        },
        // book = { product, variant, quantity }
        async addToCart({ state, commit }, book) {
            const snapshot = state.userId ? clone(state.cart) : null;
            commit('addToCart', book);
            if (!state.userId) return;
            const line = state.cart.find(item =>
                item._id === book.product._id && sameVariant(item.variant, book.variant)
            );
            try {
                await cartApi.upsert({
                    userId: state.userId,
                    bookId: book.product._id,
                    quantity: line ? line.quantity : (book.quantity ?? 1),
                    variant: book.variant,
                }, state.token);
            } catch (e) {
                commit('setCart', snapshot);
                handleApiError(commit, e, 'Could not add to cart. Please try again.');
            }
        },
        async updateCartItemQuantity({ state, commit }, payload) {
            const bookId = payload.productId ?? payload._id;
            const snapshot = state.userId ? clone(state.cart) : null;
            commit('updateCartItemQuantity', {
                productId: bookId,
                quantity: payload.quantity,
                variant: payload.variant,
            });
            if (!state.userId) return;
            try {
                await cartApi.upsert({
                    userId: state.userId,
                    bookId,
                    quantity: payload.quantity,
                    variant: payload.variant,
                }, state.token);
            } catch (e) {
                commit('setCart', snapshot);
                handleApiError(commit, e, 'Could not update quantity. Please try again.');
            }
        },
        async removeFromCart({ state, commit }, payload) {
            const bookId = payload.productId ?? payload._id;
            const variant = payload.variant;
            const snapshot = state.userId ? clone(state.cart) : null;
            commit('removeFromCart', { productId: bookId, variant });
            if (!state.userId) return;
            try {
                await cartApi.remove({ userId: state.userId, bookId, variant }, state.token);
            } catch (e) {
                commit('setCart', snapshot);
                handleApiError(commit, e, 'Could not remove item. Please try again.');
            }
        },
        clearCart({ commit }) {
            commit('clearCart');
        },
        async addToWishlist({ state, commit }, book) {
            const snapshot = state.userId ? clone(state.wishlist) : null;
            const alreadyPresent = state.wishlist.some(item =>
                item._id === book._id && sameVariant(item.variant, book.variant)
            );
            commit('addToWishlist', book);
            if (!state.userId || alreadyPresent) return;
            try {
                await wishlistApi.add({
                    userId: state.userId,
                    bookId: book._id,
                    variant: book.variant,
                }, state.token);
            } catch (e) {
                // 400 = already in wishlist server-side; keep the optimistic add.
                if (e?.response?.status === 400) return;
                commit('setWishlist', snapshot);
                handleApiError(commit, e, 'Could not add to wishlist. Please try again.');
            }
        },
        async removeFromWishlist({ state, commit }, payload) {
            // Accept a plain bookId (legacy call sites) or a full wishlist item.
            const bookId = typeof payload === 'string' ? payload : payload._id;
            const variant = typeof payload === 'string' ? undefined : payload.variant;
            const snapshot = state.userId ? clone(state.wishlist) : null;
            commit('removeFromWishlist', { bookId, variant });
            if (!state.userId) return;
            try {
                await wishlistApi.remove({ userId: state.userId, bookId, variant }, state.token);
            } catch (e) {
                commit('setWishlist', snapshot);
                handleApiError(commit, e, 'Could not remove from wishlist. Please try again.');
            }
        },
        clearWishlist({ commit }) {
            commit('clearWishlist');
        },
        loadWishlistFromStorage({ commit }) {
            commit('loadWishlistFromStorage');
        },
    },
    getters: {
        token: (state) => state.token,
        userId: (state) => state.userId,
        isSyncing: (state) => state.isSyncing,
        isLoggedIn: (state) => !!state.user,
        cartItems: (state) => state.cart,
        cartItemCount: (state) => state.cart.length,
        cartTotalPrice: (state) => {
            return state.cart.reduce((total, item) => {
                const price = item.variant?.salePrice || item.variant?.price || 0;
                return total + price * item.quantity;
            }, 0);
        },
        cartItemTotalCount: (state) => {
            return state.cart.reduce((count, item) => {
                return count + item.quantity;
            }, 0);
        },
        wishlistItems: (state) => state.wishlist,
        wishlistItemCount: (state) => state.wishlist.length,
    },
});
